import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Collapse from '@mui/material/Collapse'
import { alpha } from '@mui/material/styles'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { formatDuration } from '../duration'
import { CONTROL_RADIUS } from '../detailControls'
import { attentionReasons } from '../operationAttention'
import { SHORT_STATUS_CHANGE_SECONDS, buildStatusTimeline, firstActiveSince } from '../statusTimeline'
import ExecutionTimeCard from './ExecutionTimeCard'
import StatusChangeTable from './StatusChangeTable'
import StatusTimeline from './StatusTimeline'
import { DetailTabs, ExpansionDatum, ExpansionSection, PeopleChips } from './expansionParts'
import QualityFeatureGroups from './QualityFeatureGroups'
import { isBlank } from '../detailShared'

// S432-S439 (plan «Workflow teórico · Progress real», aprobado por el PO el 23/09) — LO REAL de una
// operación, que antes vivía en el desplegable de Workflow y se muda aquí SIN PERDER NADA (S440).
// La idea del PO: «en Workflow un vistazo de todo lo teórico, y en Progress los datos reales que se
// han ido ejecutando». Este componente es la parte que llega con N4; las cifras de N10 las pinta
// `ProgressBlock` en el titular antes de que esto cargue.

// S437 / S439 — LOS INFORMES DE INSPECCIÓN de la operación, cada uno con sus mediciones.
//
// ⚠️⚠️ NI OK NI FAIL, EN NINGÚN SITIO (decisión del PO del 23/09): el veredicto guardado es FAIL en
// todos los informes de la base del PO por una regla antigua, y pintarlo afirmaría un fallo que no
// hubo. Tampoco un estado de tolerancia (S438 retirado). Lo que se enseña es lo GUARDADO: el valor
// medido TAL CUAL —es texto: un número, un lote; las respuestas sí/no, como OK/NOK—, y el nominal y las tolerancias SOLO si
// vienen. Sus columnas aparecen si alguna medición del informe las trae, y si no, no ocupan sitio:
// la misma regla que el motivo de los tramos de estado.
// S437 / S439 — LOS INFORMES DE INSPECCIÓN de la operación: UN BLOQUE POR INFORME, en el orden de Sun,
// cada uno como el widget de inspección de MOS (`QualityFeatureGroups`, modo medido): todas las
// características de la tarea y, al lado, lo que se midió para esa pieza. Las que no se midieron salen
// ENTERAS y dicen «sin medir»: no desaparecen, y así se ve que están pendientes.
// ⚠️⚠️ NI OK NI FAIL, en ningún sitio (PO, 23/09): MOS tampoco lo enseña, y el veredicto guardado es
// FAIL siempre en la base del PO por una regla antigua.
// ⚠️ Las REINSPECCIONES son reales —20 de 130 pares (pieza, tarea) en la base del PO, hasta 5; en LTT002
// los informes 156 y 157 son de la misma pieza y tarea con dos minutos de diferencia—: cada una es su
// bloque, y la cabecera lleva FECHA Y HORA para distinguirlas. Fundirlas obligaría a decidir cuál vale.
// ⚠️ La cabecera NO dice «sin operario»: `worker` viene `null` en el 100 % de los informes, y el único
// «quién midió» —`responsible`— va en su columna, medición a medición.
const InspectionReports = ({ quality }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const notReported = t_('Not reported')
  // Only the first report opens by itself: a full 13-column table per report pushed everything else out
  // of sight. The others open from their heading, which says date, task and device.
  const [openIds, setOpenIds] = useState({})
  if (quality.status === 'loading') {
    return <Typography data-testid='inspection-reports-loading' variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
  }
  if (quality.status === 'error') {
    return <Typography data-testid='inspection-reports-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load this block')}</Typography>
  }
  const reports = (quality.data || {}).inspectionReports || []
  const isOpen = (report, index) => (openIds[report.inspectionReportId] === undefined ? index === 0 : openIds[report.inspectionReportId])
  // Una operación SIN INFORMES no ha fallado nada: se dice con palabras, y en gris.
  if (reports.length === 0) {
    return <Typography data-testid='inspection-reports-empty' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No inspection reports')}</Typography>
  }
  return (
    <Stack spacing={3} data-testid='inspection-reports'>
      {reports.map((report, index) => {
        const open = isOpen(report, index)
        const prefix = `inspection-report-${report.inspectionReportId}`
        return (
          <Box key={report.inspectionReportId} data-testid={prefix}>
            <ButtonBase
              data-testid={`${prefix}-toggle`}
              aria-expanded={open}
              aria-controls={`${prefix}-content`}
              onClick={() => setOpenIds((prev) => ({ ...prev, [report.inspectionReportId]: !open }))}
              sx={{ display: 'block', textAlign: 'left', mb: 1 }}
            >
              <Typography data-testid='inspection-report-heading' variant='body2' sx={{ fontWeight: 700 }}>
                {[
                  `${t_('Part')} ${isBlank(report.partId) ? notReported : report.partId}`,
                  isBlank(report.reportDate) ? notReported : formatDateTime(report.reportDate),
                  report.task.name || notReported,
                  report.deviceName || notReported
                ].join(' · ')}
              </Typography>
            </ButtonBase>
            <Collapse in={open} unmountOnExit>
              <Box id={`${prefix}-content`}>
                <QualityFeatureGroups featureGroups={report.featureGroups} mode='measured' testIdPrefix={prefix} />
              </Box>
            </Collapse>
          </Box>
        )
      })}
    </Stack>
  )
}

/**
 * @param {{data: object, operation: object, quality: {status: string, data: object}, reportsContent: React.ReactNode, reportsCount: number, resourcesContent: React.ReactNode}} props
 *   `data` es la respuesta de N4 YA MAPEADA; `operation` es la fila de N10; `quality`, la de su
 *   propia puerta (S437/S439), con su estado de carga.
 */
const ProgressOperationDetails = ({ data, operation = {}, figures = {}, quality = { status: 'loading', data: { inspectionReports: [] } }, reportsContent = null, reportsCount = 0, resourcesContent = null }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const notReported = t_('Not reported')
  // S401 → S433 — UN SOLO INTERRUPTOR PARA LA LÍNEA DE TIEMPO Y LA TABLA: con uno en cada vista el
  // usuario podría dejarlas en estados distintos y leer dos recuentos que no cuadran.
  const [includeShort, setIncludeShort] = useState(false)
  const statusChanges = data.statusChanges || []
  // Built once and shared with the timeline: it also gives the count of the hidden short changes.
  const timeline = buildStatusTimeline(statusChanges, { includeShort })
  const hiddenShortCount = timeline.hiddenCount
  const reporters = data.reporters || []
  const resources = operation.resources || []
  const lastReport = data.lastReport || {}

  // S432 — «en marcha desde…», de `statusChanges` y no de `workers` (el porqué, en
  // `firstActiveSince`). Es también el «desde» del aviso de atención: una sola fecha para los dos.
  const activeSince = firstActiveSince(statusChanges)

  // ADDENDUM 45 — «Requiere atención», con las MISMAS reglas que la píldora de la fila de Workflow
  // (`operationAttention`), no una copia. ⚠️⚠️ Add. 64 H/I — y con las MISMAS cifras que la fila:
  // `figures` es `rowCellsSource`, el teórico de N3 y el real de N10. Hasta el 23/09 leía los dos de
  // N4, y la fila y el aviso podían decir cosas distintas de la misma operación.
  const avisos = attentionReasons({
    quantityDone: operation.done,
    theoreticalDurationSeconds: figures.theoreticalDurationSeconds,
    actualDurationSeconds: figures.actualDurationSeconds
  }, activeSince)
  const textoAviso = (aviso) => {
    if (aviso.key === 'over-theoretical') return t_('the actual time exceeds the theoretical by {{amount}}', { amount: formatDuration(aviso.deltaSeconds) })
    return isBlank(aviso.since) ? t_('Pending to report') : t_('Pending to report since {{date}}', { date: formatDateTime(aviso.since) })
  }

  const tabs = [
    {
      key: 'reports',
      label: t_('Reports'),
      count: reportsCount,
      content: (
        <Stack spacing={1.5}>
          <ExpansionSection columns={3}>
            <ExpansionDatum label={t_('Reports')} testId='expansion-report-count'>
              {isBlank(data.reportCount) ? notReported : data.reportCount}
            </ExpansionDatum>
            <Box sx={{ gridColumn: { lg: 'span 2' } }}>
              <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}>
                {t_('Have reported')}
              </Typography>
              {/* ⚠️ Son TRABAJADORES, no usuarios (addendum 16 D). */}
              <PeopleChips
                testIdPrefix='expansion-reporter'
                people={reporters}
                emptyTestId='expansion-no-reporters'
                emptyLabel={notReported}
                keyOf={(reporter) => reporter.workerId}
                labelOf={(reporter) => `${reporter.workerName}: ${reporter.count}`}
              />
            </Box>
          </ExpansionSection>
          {reportsContent}
        </Stack>
      )
    },
    {
      // S433 — lo de S422 TAL CUAL QUEDÓ, movido: línea de tiempo, filtro de 1 min y tabla.
      key: 'states',
      label: t_('States'),
      count: statusChanges.length,
      content: (
        <Stack spacing={1.5}>
          <StatusTimeline statusChanges={statusChanges} includeShort={includeShort} timeline={timeline} />
          {/* ⚠️ S401: OCULTAR NO ES AUSENCIA (RN8) — lo escondido se cuenta y se puede recuperar. Se
              pinta siempre que haya tramos, aunque no esconda ninguno, para que se sepa que hay un
              umbral. Los tramos ABIERTOS no se esconden nunca. */}
          {statusChanges.length > 0 && (
            <Button
              data-testid='short-status-changes-toggle'
              size='small'
              variant='text'
              onClick={() => setIncludeShort((prev) => !prev)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: CONTROL_RADIUS }}
            >
              {includeShort
                ? t_('Hide status changes shorter than {{threshold}}', { threshold: formatDuration(SHORT_STATUS_CHANGE_SECONDS) })
                : t_('Show status changes shorter than {{threshold}} ({{count}} hidden)', { threshold: formatDuration(SHORT_STATUS_CHANGE_SECONDS), count: hiddenShortCount })}
            </Button>
          )}
          <StatusChangeTable statusChanges={statusChanges} includeShort={includeShort} />
        </Stack>
      )
    },
    // (S434 RETIRADO, PO 23/09: aquí estuvo «Who changed the state», quién abrió y cerró los tramos.
    // No aportaba información útil y se quitó entera, con su tabla y su agregado.)
    {
      // S435 — los materiales consumidos de S421, movidos.
      key: 'resources',
      label: t_('Resources'),
      count: resources.length,
      content: resourcesContent
    },
    {
      // S437/S439 — los informes de inspección con sus mediciones, de su propia puerta, al modelo de MOS.
      // El recuento es el de INFORMES; mientras carga no hay número que dar, y se dice «…», no un cero.
      // ⚠️ Aquí hubo un resumen de autocontroles «ok/total» que venía del plegable de Workflow: se
      // retiró (PO, 23/09) porque era el veredicto FAIL resumido. S436 también se retiró.
      key: 'quality',
      label: t_('Quality'),
      count: quality.status === 'ready' ? ((quality.data || {}).inspectionReports || []).length : '…',
      content: <InspectionReports quality={quality} />
    }
  ]

  return (
    <Stack spacing={2} data-testid={`progress-operation-details-${operation.jobReferenceId}`}>
      {/* ⚠️⚠️ ADDENDUM 45 — «REQUIERE ATENCIÓN». No inventa ninguna alerta: los dos motivos se leen
          juntos y arriba. Las reglas se importan de donde viven, no se reescriben aquí. */}
      {avisos.length > 0 && (
        <Stack
          data-testid='expansion-attention'
          direction='row'
          spacing={1.5}
          alignItems='center'
          sx={(theme) => ({ p: 1.5, borderRadius: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}` })}
        >
          <WarningAmberRounded fontSize='small' sx={{ color: 'warning.main' }} />
          <Typography variant='body2' sx={{ fontWeight: 800, color: 'warning.main', whiteSpace: 'nowrap' }}>{t_('Requires attention')}</Typography>
          <Typography data-testid='expansion-attention-reasons' variant='body2' sx={{ color: 'text.primary' }}>
            {avisos.map((aviso) => textoAviso(aviso)).join(' · ')}
          </Typography>
        </Stack>
      )}

      {/* S432 — el resto del titular: desde cuándo está en marcha y cuál fue el último reporte. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }}>
        <Typography data-testid='headline-active-since' variant='body2' sx={{ color: 'text.secondary' }}>
          {/* Sin paso a un estado activo NO hay «desde»: se dice, no se inventa una fecha. */}
          {isBlank(activeSince) ? t_('Never in progress') : t_('In progress since {{date}}', { date: formatDateTime(activeSince) })}
        </Typography>
        <Typography data-testid='headline-last-report' variant='body2' sx={{ color: 'text.secondary' }}>
          {isBlank(lastReport.at)
            ? t_('No reports yet')
            : t_('Last report: {{date}} · {{worker}}', { date: formatDateTime(lastReport.at), worker: lastReport.workerName || lastReport.workerCode || notReported })}
        </Typography>
      </Stack>

      {/* Tiempo real y su desviación (D1): la tarjeta de ADDENDUM 45. ⚠️ Add. 64 H/I: teórico de N3 y
          real de N10, los de la fila; lo programado solo lo trae N4 y de ahí se sigue leyendo. */}
      <ExecutionTimeCard data={{ ...data, theoreticalDurationSeconds: figures.theoreticalDurationSeconds, actualDurationSeconds: figures.actualDurationSeconds }} />

      <DetailTabs testId={`progress-operation-tabs-${operation.jobReferenceId}`} tabs={tabs} />
    </Stack>
  )
}

export default ProgressOperationDetails
